import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";

interface CopyableRowProps {
  label: string;
  value: string | null | undefined;
}

/**
 * Label/value row with click- or keyboard-triggered copy-to-clipboard and a
 * transient copied indicator. Must be rendered inside TooltipBody, which
 * provides the .row/.label/.copyable-value/.copy-icon styles.
 *
 * Deliberately separate from EntityInfo/CopyableDetailRow: that one is
 * self-styled for the info cards (0.6rem bold labels, whole row clickable,
 * renders "None" for empty values). This row inherits the tooltip's 12px
 * layout, makes only the value clickable, and hides entirely when empty so
 * optional rows (location group, office) don't leave "None" lines in the
 * hover. Merging them would mean per-context style overrides on the card
 * component; not worth it for two consumers.
 */
const CopyableRow: React.FC<CopyableRowProps> = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  if (!value) return null;

  const handleCopy = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCopy(e);
    }
  };

  return (
    <div className="row">
      <span className="label">{label}</span>
      <div
        className="copyable-value"
        onClick={handleCopy}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label={`Copy ${label}: ${value}`}
      >
        <span className="value">{value}</span>
        <span className="copy-icon" data-copied={copied}>
          {copied ? <Check size={11} /> : <Copy size={11} />}
        </span>
      </div>
    </div>
  );
};

export default CopyableRow;
